import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  financeStructureApi,
  formatAgorot,
  shekelsToAgorot,
  type BudgetOverview,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";

type Props = { writeCustomerId: string };

export function BudgetsTab({ writeCustomerId }: Props) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [scope, setScope] = useState<"month" | "year">("month");
  const [overview, setOverview] = useState<BudgetOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [fromCategoryId, setFromCategoryId] = useState("");
  const [toCategoryId, setToCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeStructureApi.budgetOverview({
        year,
        month: scope === "month" ? month : undefined,
        scope,
      });
      setOverview(data);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [year, month, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "displayName",
        title: "קטגוריה",
        sortable: true,
        filterable: true,
        minWidth: 160,
      },
      {
        key: "incomeTarget",
        title: "יעד הכנסה",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.incomeTarget ?? 0)),
      },
      {
        key: "incomeActual",
        title: "הכנסה בפועל",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.incomeActual ?? 0)),
      },
      {
        key: "incomeProgressPct",
        title: "% הכנסה",
        render: (_value: unknown, row: Record<string, unknown>) => {
          const pct = Number(row.incomeProgressPct ?? 0);
          return (
            <div className="fs-progress" title={`${pct}%`}>
              <div
                className="fs-progress__bar"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          );
        },
      },
      {
        key: "effectiveExpenseBudget",
        title: "תקציב הוצאה",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.effectiveExpenseBudget ?? 0)),
      },
      {
        key: "expenseActual",
        title: "הוצאה בפועל",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.expenseActual ?? 0)),
      },
      {
        key: "expenseProgressPct",
        title: "% הוצאה",
        render: (_value: unknown, row: Record<string, unknown>) => {
          const pct = Number(row.expenseProgressPct ?? 0);
          const over = Boolean(row.isOverBudget);
          const near = Boolean(row.isNearBudget);
          return (
            <div className="fs-progress" title={`${pct}%`}>
              <div
                className={`fs-progress__bar${over ? " fs-progress__bar--danger" : near ? " fs-progress__bar--warn" : ""}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          );
        },
      },
    ],
    [],
  );

  const totals = overview?.totals;

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        secondary={
          <>
            <FormField
              label="תקופה"
              type="select"
              value={scope}
              onChange={(value) => setScope(value as "month" | "year")}
              options={[
                { value: "month", label: "חודשי" },
                { value: "year", label: "שנתי" },
              ]}
            />
            <FormField
              label="שנה"
              type="select"
              value={String(year)}
              onChange={(value) => setYear(Number(value))}
              options={Array.from({ length: 7 }, (_, i) => {
                const y = now.getFullYear() - 3 + i;
                return { value: String(y), label: String(y) };
              })}
            />
            {scope === "month" ? (
              <FormField
                label="חודש"
                type="select"
                value={String(month)}
                onChange={(value) => setMonth(Number(value))}
                options={Array.from({ length: 12 }, (_, i) => {
                  const m = i + 1;
                  return { value: String(m), label: String(m) };
                })}
              />
            ) : null}
            <SettingsButton
              type="button"
              variant="settings"
              size="sm"
              onClick={() => {
                const cats = overview?.categories ?? [];
                setFromCategoryId(cats[0]?.id ?? "");
                setToCategoryId(cats[1]?.id ?? cats[0]?.id ?? "");
                setAmount("");
                setTransferOpen(true);
              }}
            >
              העברת תקציב
            </SettingsButton>
          </>
        }
      />
      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p>טוען…</p> : null}

      {totals ? (
        <div className="fs-budget-stats">
          <div className="fs-budget-stat">
            <div className="fs-budget-stat__label">יעד הכנסה</div>
            <div className="fs-budget-stat__value">
              {formatAgorot(totals.incomeTarget)}
            </div>
          </div>
          <div className="fs-budget-stat">
            <div className="fs-budget-stat__label">הכנסה בפועל</div>
            <div className="fs-budget-stat__value">
              {formatAgorot(totals.incomeActual)}
            </div>
          </div>
          <div className="fs-budget-stat">
            <div className="fs-budget-stat__label">תקציב הוצאה</div>
            <div className="fs-budget-stat__value">
              {formatAgorot(totals.expenseBudget)}
            </div>
          </div>
          <div className="fs-budget-stat">
            <div className="fs-budget-stat__label">הוצאה בפועל</div>
            <div className="fs-budget-stat__value">
              {formatAgorot(totals.expenseActual)}
            </div>
          </div>
          <div className="fs-budget-stat">
            <div className="fs-budget-stat__label">חריגות</div>
            <div className="fs-budget-stat__value">{totals.overBudgetCount}</div>
          </div>
        </div>
      ) : null}

      {overview?.alerts?.length ? (
        <div className="fs-alerts">
          {overview.alerts.map((a, i) => (
            <div key={`${a.categoryId}-${i}`} className={`fs-alert fs-alert--${a.severity}`}>
              {a.message}
            </div>
          ))}
        </div>
      ) : null}

      {!loading && overview ? (
        <>
          <TableKit
            columns={columns}
            rows={overview.categories as unknown as Record<string, unknown>[]}
            rowKey="id"
            emptyMessage="אין קטגוריות עם תקציב"
          />
          <h3 style={{ marginTop: "1.5rem" }}>העברות תקציב</h3>
          <TableKit
            columns={[
              {
                key: "fromCategoryId",
                title: "מקטגוריה",
                render: (_value: unknown, row: Record<string, unknown>) =>
                  overview.categories.find((c) => c.id === row.fromCategoryId)?.name ??
                  String(row.fromCategoryId),
              },
              {
                key: "toCategoryId",
                title: "לקטגוריה",
                render: (_value: unknown, row: Record<string, unknown>) =>
                  overview.categories.find((c) => c.id === row.toCategoryId)?.name ??
                  String(row.toCategoryId),
              },
              {
                key: "amountAgorot",
                title: "סכום",
                render: (_value: unknown, row: Record<string, unknown>) =>
                  formatAgorot(Number(row.amountAgorot ?? 0)),
              },
              {
                key: "actions",
                title: "",
                render: (_value: unknown, row: Record<string, unknown>) => (
                  <QuietButton
                    type="button"
                    onClick={() => {
                      const t = row as {
                        id: string;
                        customerId: string;
                      };
                      void financeStructureApi
                        .deleteBudgetTransfer(t.customerId, t.id)
                        .then(load)
                        .catch((e) => setError(toClientApiError(e).message));
                    }}
                  >
                    מחיקה
                  </QuietButton>
                ),
              },
            ]}
            rows={overview.transfers as unknown as Record<string, unknown>[]}
            rowKey="id"
            emptyMessage="אין העברות תקציב"
          />
        </>
      ) : null}

      <FormDialog open={transferOpen} onClose={() => setTransferOpen(false)} size="md">
        <FormCard
          title="העברת תקציב"
          onSave={() => {
            void (async () => {
              setSaving(true);
              try {
                const from = overview?.categories.find((c) => c.id === fromCategoryId);
                const to = overview?.categories.find((c) => c.id === toCategoryId);
                if (!from || !to) {
                  throw new Error("בחרו קטגוריות מקור ויעד");
                }
                await financeStructureApi.createBudgetTransfer(writeCustomerId, {
                  fromCategoryId,
                  toCategoryId,
                  amountAgorot: shekelsToAgorot(Number(amount)),
                  year,
                  month: scope === "month" ? month : null,
                  scope,
                });
                setTransferOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setTransferOpen(false)}
          saveLabel="העברה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !amount || Number(amount) <= 0}
        >
          <FormSection title="העברה">
            <FormGrid>
              <FormField
                label="מקטגוריה"
                value={fromCategoryId}
                onChange={setFromCategoryId}
                options={(overview?.categories ?? []).map((c) => ({
                  value: c.id,
                  label: c.displayName,
                }))}
              />
              <FormField
                label="לקטגוריה"
                value={toCategoryId}
                onChange={setToCategoryId}
                options={(overview?.categories ?? []).map((c) => ({
                  value: c.id,
                  label: c.displayName,
                }))}
              />
              <FormField
                label="סכום (₪)"
                required
                type="number"
                value={amount}
                onChange={setAmount}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </div>
  );
}
